"use client";

import { clsx } from "clsx";
import { Menu, X } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useEffect, useState } from "react";

import { links } from "@/lib/links";

const navLinks = [
  { href: "/#how-it-works", label: "Product" },
  { href: "/#voice", label: "Voice" },
  { href: "/#specialties", label: "Specialties" },
  { href: "/pricing", label: "Pricing" },
  { href: "/#security", label: "Security" },
  { href: "/contact", label: "Contact" },
];

// A frosted pill that stays put while the page scrolls. It firms up (more opaque, a shadow) once the
// page has scrolled, so it stays legible over the product screens.
export default function NavbarMarketing() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="sticky top-3 z-50 px-4 pt-3">
      <nav
        aria-label="Main"
        className={clsx(
          "mx-auto max-w-6xl rounded-[28px] px-3 py-2 transition-[background-color,box-shadow] duration-300 sm:pl-5",
          scrolled || open ? "glass-strong" : "glass"
        )}
      >
        <div className="flex items-center gap-4">
          <Link href="/" aria-label="Refdocs home" className="flex shrink-0 items-center rounded-md">
            <Image src="/imgs/Refdocs_Logo_Colour_SVG.svg" alt="Refdocs" width={123} height={25} priority unoptimized className="h-[25px] w-auto" />
          </Link>

          <div className="ml-4 hidden items-center gap-1 lg:flex">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                className="rounded-full px-3 py-1.5 text-[15px] text-body transition-colors hover:bg-white/70 hover:text-ink"
              >
                {link.label}
              </Link>
            ))}
          </div>

          <div className="ml-auto hidden items-center gap-2 sm:flex">
            <a href={links.signin()} className="rounded-full px-3 py-1.5 text-[15px] text-ink transition-colors hover:bg-white/70">
              Sign in
            </a>
            <a
              href={links.trial()}
              className="rounded-full bg-ink px-4 py-2 text-[15px] font-medium text-white shadow-[0_10px_24px_-10px_rgba(11,18,64,0.6)] transition-colors hover:bg-accentStrong"
            >
              Start free trial
            </a>
          </div>

          <button
            type="button"
            className="ml-auto inline-flex h-10 w-10 items-center justify-center rounded-full text-ink hover:bg-white/70 sm:ml-0 lg:hidden"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            aria-controls="mobile-menu"
            aria-label={open ? "Close menu" : "Open menu"}
          >
            {open ? <X size={20} aria-hidden /> : <Menu size={20} aria-hidden />}
          </button>
        </div>

        <div id="mobile-menu" hidden={!open} className="lg:hidden">
          <div className="mt-2 grid gap-1 border-t border-hair px-1 pb-2 pt-3">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                onClick={() => setOpen(false)}
                className="rounded-xl px-3 py-2.5 text-base text-ink hover:bg-white/70"
              >
                {link.label}
              </Link>
            ))}
            <div className="mt-2 flex items-center gap-2 sm:hidden">
              <a href={links.signin()} className="flex-1 rounded-full border border-hair bg-white/70 px-4 py-2.5 text-center text-[15px] text-ink">
                Sign in
              </a>
              <a href={links.trial()} className="flex-1 rounded-full bg-ink px-4 py-2.5 text-center text-[15px] font-medium text-white">
                Start free trial
              </a>
            </div>
          </div>
        </div>
      </nav>
    </div>
  );
}
